/* ═══════════════════════════════════════════════════════════════
   PURLA — AI motion layer (vanilla JS, no deps)
   1) Boomerang video: captures a section's <video> into canvas frames
      and plays them forward↔reverse in a seamless loop (ported from
      the React BoomerangVideoBg reference to plain JS).
   2) Magnetic CTAs: primary buttons drift toward the cursor (fine
      pointers only), eased via lerp outside React's render cycle.
   Progressive enhancement: if anything fails, the native video keeps
   playing and buttons stay static. Honors prefers-reduced-motion.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (window.__purlaMotion) return;
  window.__purlaMotion = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ── 1 · BOOMERANG VIDEO ─────────────────────────────────────── */
  function boomerang(video) {
    if (!video || video.dataset.purlaBoomerang === 'done') return;
    video.dataset.purlaBoomerang = 'done';

    var MAX_WIDTH = 300;           // downscale: bg loop, source is 404w portrait
    var MAX_FRAMES = 110;          // memory cap (~3.6s each way, ~50MB)
    var frames = [];
    var capturing = true;
    var lastTime = -1;

    function capture() {
      if (!capturing || video.readyState < 2) return;
      if (video.currentTime === lastTime) return;
      lastTime = video.currentTime;
      var vw = video.videoWidth, vh = video.videoHeight;
      if (!vw || !vh) return;
      var scale = Math.min(1, MAX_WIDTH / vw);
      var w = Math.round(vw * scale), h = Math.round(vh * scale);
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d');
      if (!ctx) return;
      try { ctx.drawImage(video, 0, 0, w, h); } catch (e) { capturing = false; return; }
      frames.push(c);
      if (frames.length >= MAX_FRAMES) finish();
    }

    var hasVFC = typeof video.requestVideoFrameCallback === 'function';
    var rafId = 0;
    function rafLoop() { capture(); if (capturing) rafId = requestAnimationFrame(rafLoop); }
    function vfcLoop() { capture(); if (capturing) video.requestVideoFrameCallback(vfcLoop); }

    function finish() {
      if (!capturing) return;
      capturing = false;
      if (frames.length < 8) return;         // not enough to loop; keep native video
      startPlayback();
    }

    function startPlayback() {
      var first = frames[0];
      var canvas = document.createElement('canvas');
      canvas.width = first.width; canvas.height = first.height;
      canvas.className = video.className;
      var vs = video.getAttribute('style') || '';
      canvas.setAttribute('style', vs);
      canvas.style.display = 'block';
      canvas.setAttribute('aria-hidden', 'true');
      video.parentNode.insertBefore(canvas, video.nextSibling);
      video.style.display = 'none';
      try { video.pause(); } catch (e) {}

      var ctx = canvas.getContext('2d');
      var i = 0, dir = 1, last = performance.now(), interval = 1000 / 30, run = true;

      // pause when offscreen / tab hidden (perf)
      var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (es) {
        run = es[0].isIntersecting;
        if (run) requestAnimationFrame(render);
      }, { threshold: 0.01 }) : null;
      if (io) io.observe(canvas);
      document.addEventListener('visibilitychange', function () {
        run = !document.hidden;
        if (run) requestAnimationFrame(render);
      });

      function render(now) {
        if (!run) return;
        if (now - last >= interval) {
          last = now;
          ctx.drawImage(frames[i], 0, 0);
          i += dir;
          if (i >= frames.length - 1) { i = frames.length - 1; dir = -1; }
          else if (i <= 0) { i = 0; dir = 1; }
        }
        requestAnimationFrame(render);
      }
      requestAnimationFrame(render);
    }

    var started = false;
    function begin() {                         // called once the section is near view
      if (started) return; started = true;
      try {
        if (!video.src && !video.currentSrc) {   // .lazy-video: promote data-src → src
          var ds = video.getAttribute('data-src');
          if (ds) video.src = (ds.indexOf('//') === 0 ? 'https:' : '') + ds;
        }
        video.setAttribute('preload', 'auto'); video.muted = true; video.load();
      } catch (e) {}
      var p = video.play(); if (p && p.catch) p.catch(function () {});
      if (hasVFC) video.requestVideoFrameCallback(vfcLoop); else rafId = requestAnimationFrame(rafLoop);
      video.addEventListener('ended', finish);
      setTimeout(finish, 14000);               // safety once capture has begun
    }
    boomerang._begin = begin;
  }

  function initBoomerang() {
    if (reduce || !finePointer) return;        // reduced motion / mobile → native looping video (protect memory)
    var host = document.querySelector('[id*="video_with_text_overlay_3iMkPg"]');
    if (!host) return;

    function attach(v) {
      boomerang(v);
      var begin = boomerang._begin; if (!begin) return;
      var fired = false;
      function trigger() { if (fired) return; fired = true; begin(); }
      // Start capture when the banner actually enters view — offscreen videos are
      // throttled by the browser and won't advance frames. IO + a scroll fallback.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es) {
          if (es[0].isIntersecting) { io.disconnect(); trigger(); }
        }, { threshold: 0.15 });
        io.observe(host);
      }
      function onScroll() {
        var r = host.getBoundingClientRect();
        if (r.top < window.innerHeight * 1.2 && r.bottom > 0) {
          window.removeEventListener('scroll', onScroll); trigger();
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    var v = host.querySelector('video');
    if (v) attach(v);
    else {                                      // video may lazy-mount into the DOM
      var mo = new MutationObserver(function () {
        var vv = host.querySelector('video');
        if (vv) { mo.disconnect(); attach(vv); }
      });
      mo.observe(host, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 10000);
    }
  }

  /* ── 2 · MAGNETIC CTAs ───────────────────────────────────────── */
  function initMagnetic() {
    if (reduce || !finePointer) return;
    var sels = '.btn-primary, .image-comparison__button + a, [id*="image_comparison"] a[class*="btn"]';
    var btns = Array.prototype.slice.call(document.querySelectorAll(sels)).slice(0, 12);
    var STRENGTH = 0.3, MAXP = 7;
    btns.forEach(function (btn) {
      if (btn.dataset.purlaMag) return;
      btn.dataset.purlaMag = '1';
      // Direct transform, smoothed by the button's existing CSS transition
      // (transform 150ms ease-out). Interruptible + retargets cleanly (Emil).
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var tx = Math.max(-MAXP, Math.min(MAXP, (e.clientX - (r.left + r.width / 2)) * STRENGTH));
        var ty = Math.max(-MAXP, Math.min(MAXP, (e.clientY - (r.top + r.height / 2)) * STRENGTH));
        btn.style.transform = 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px)';
      });
      btn.addEventListener('pointerleave', function () { btn.style.transform = ''; });
    });
  }

  /* ── 3 · TRUST LINE under add-to-cart (bilingual reassurance) ── */
  function initTrust() {
    // PRODUCT PAGES ONLY — never inject under homepage/collection product cards
    if (!/\/products\//.test(location.pathname)) return;
    var btn = document.querySelector('.product_submit_button, [class*="product__add-cart"]');
    if (!btn) return;
    // insert the panel AFTER the button row (full-width below), not inside it
    var row = btn.closest('.product-form__buttons') || btn.closest('.product-form') || btn.parentElement;
    var host = row.parentElement || row;
    if (!host || host.querySelector('.purla-trust')) return;
    var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
    var truck = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/></svg>';
    var cash = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="6" width="19" height="12" rx="2"/><circle cx="12" cy="12" r="2.4"/></svg>';
    var shield = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>';
    var items = ar
      ? [[truck, 'توصيل خلال ٥ أيام عمل'], [cash, 'دفع عند الاستلام'], [shield, 'ضمان استرجاع ٣٠ يوم']]
      : [[truck, 'Delivery in 5 working days'], [cash, 'Cash on delivery'], [shield, '30-day guarantee']];
    var el = document.createElement('div');
    el.className = 'purla-trust';
    el.setAttribute('dir', ar ? 'rtl' : 'ltr');
    el.innerHTML = items.map(function (i) { return '<span>' + i[0] + i[1] + '</span>'; }).join('');
    if (row.nextSibling) host.insertBefore(el, row.nextSibling); else host.appendChild(el);
  }

  /* ── 4 · CALM REVEAL — product-page story media ──────────────────
     Gentle staggered entrance on the media below the buy box. Content is
     visible by default (CSS hides only [data-pl-reveal] under .pl-motion,
     a class we add here); if this script never runs, nothing is hidden.
     A safety timer force-reveals anything IO hasn't reached. */
  function initReveal() {
    // group selector → each group's direct tiles get a within-group stagger
    var groups = [
      '[id*="multi_content_DJeAL9"] .section__block',
      '[id*="multi_content_bGxkXe"] .section__block',
      '[id*="multi_content_it3DUP"] .section__block',
      '[id*="shopable_video_UeLeiL"] .fluid_container',
      '[id*="image_with_text_overlay_pbPq"] .sec__inner'
    ];
    var tiles = [];
    groups.forEach(function (sel) {
      var found = Array.prototype.slice.call(document.querySelectorAll(sel));
      found.forEach(function (el, i) {
        if (el.hasAttribute('data-pl-reveal')) return;
        el.setAttribute('data-pl-reveal', '');
        // stagger within this group only (capped so late tiles don't lag)
        el.style.setProperty('--pl-reveal-delay', Math.min(i * 90, 450) + 'ms');
        tiles.push(el);
      });
    });
    if (!tiles.length) return;               // not a product page → do nothing
    document.documentElement.classList.add('pl-motion');

    function show(el) { el.classList.add('is-in'); }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { show(e.target); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      tiles.forEach(function (t) { io.observe(t); });
    } else {
      tiles.forEach(show);
    }
    // safety net: never leave a tile stuck hidden (IO edge cases, bg tabs)
    setTimeout(function () { tiles.forEach(show); }, 4000);
  }

  /* ── 5 · PRODUCT PROOF — replace baked-text image grid with clean HTML ──
     The old multi_content_DJeAL9 grid used ad images with text burned in
     (numbers cropped at the edges, English on an Arabic store, ragged odd
     count). We render the same proof as real, localized text — a count-up
     stat row + a testimonial — and hide the old grid. Product pages only. */
  function initProof() {
    if (!/\/products\//.test(location.pathname)) return;
    var host = document.querySelector('[id*="multi_content_DJeAL9"]');
    if (!host || host.dataset.purlaProofDone) return;
    host.dataset.purlaProofDone = '1';

    var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
    var pct = '%';
    function ad(s) { return String(s); }   // Western numerals (per brand choice)

    var stats = ar
      ? [[95, pct, 'نسبة إزالة الكلورين من المياه'], [87, pct, 'بشرة صافية بلا جفاف'], [2, '×', 'ضغط ماء أقوى']]
      : [[95, pct, 'chlorine removed from water'], [87, pct, 'clearer skin, without dryness'], [2, '×', 'stronger water pressure']];
    var title = ar ? 'نتائج حقيقية، ملموسة' : 'Real, measurable results';
    var quote = ar
      ? 'شعري وبشرتي ما حسّيت فيهم بهالنعومة من قبل. فرق واضح من أول أسبوع، بدون تساقط ولا تقصّف.'
      : 'My hair and skin have never felt this healthy. A clear difference from the first week, no fallout or breakage.';
    var who = ar ? 'نورة · مشترية مؤكَّدة' : 'Nora · Verified buyer';

    var sec = document.createElement('section');
    sec.className = 'purla-proof';
    sec.setAttribute('dir', ar ? 'rtl' : 'ltr');
    var statsHtml = stats.map(function (s) {
      return '<div class="purla-proof__stat"><div class="purla-proof__num" data-target="' + s[0] +
        '" data-suffix="' + s[1] + '">' + ad(0) + s[1] + '</div><p class="purla-proof__label">' + s[2] + '</p></div>';
    }).join('');
    sec.innerHTML = '<h2 class="purla-proof__title">' + title + '</h2>' +
      '<div class="purla-proof__stats">' + statsHtml + '</div>' +
      '<figure class="purla-proof__quote"><div class="purla-proof__stars" aria-hidden="true">★★★★★</div>' +
      '<blockquote>' + quote + '</blockquote><figcaption>' + who + '</figcaption></figure>';

    host.parentNode.insertBefore(sec, host);
    host.style.display = 'none';          // hide the old baked-text grid

    function counted() {
      sec.querySelectorAll('.purla-proof__num').forEach(function (n) {
        var target = parseInt(n.getAttribute('data-target'), 10), suf = n.getAttribute('data-suffix');
        if (reduce) { n.textContent = ad(target) + suf; return; }
        var dur = 1100, start = null;
        function fr(t) {
          if (!start) start = t;
          var p = Math.min((t - start) / dur, 1), e = 1 - Math.pow(1 - p, 3);
          n.textContent = ad(Math.round(e * target)) + suf;
          if (p < 1) requestAnimationFrame(fr);
        }
        requestAnimationFrame(fr);
        // guarantee the final value even if rAF is throttled (bg/offscreen tab)
        setTimeout(function () { n.textContent = ad(target) + suf; }, dur + 60);
      });
    }
    function reveal() { sec.classList.add('is-in'); counted(); }

    if (reduce) { reveal(); return; }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { io.disconnect(); reveal(); }
      }, { threshold: 0.2 });
      io.observe(sec);
    } else { reveal(); }
    setTimeout(function () { if (!sec.classList.contains('is-in')) reveal(); }, 3500);
  }

  /* ── 6 · SOCIAL-PROOF RATING BAND — replaces the cartoon banner ──
     Big aggregate score (count-up) + twinkling stars + an auto-scrolling
     review ticker that pauses on hover. Always-on motion + interactive.
     Hides image_with_text_overlay (the 019.jpg cartoon). Product pages only. */
  function initRatingBand() {
    if (!/\/products\//.test(location.pathname)) return;
    var host = document.querySelector('[id*="image_with_text_overlay_pbPq"]');
    if (!host || host.dataset.purlaRatingDone) return;
    host.dataset.purlaRatingDone = '1';

    var ar = (document.documentElement.lang || '').indexOf('ar') === 0;
    var score = 4.9;
    var countLine = ar ? 'من 5 · تقييمات موثّقة من عملائنا' : 'out of 5 · from verified customers';
    var reviews = ar ? [
      ['بشرتي صارت أنعم بوضوح', 'نورة، الكويت'],
      ['أفضل شي اشتريته لحمامي', 'سارة، الكويت'],
      ['الفرق واضح من أول أسبوع', 'لينا، دبي'],
      ['شعري أقل تساقطًا والماء أنقى', 'عبدالله، الرياض'],
      ['التركيب سهل والنتيجة ممتازة', 'مريم، الدوحة'],
      ['ما أتوقع أرجع أستحم بدونه', 'هيا، الكويت'],
      ['رائحة الكلور اختفت تمامًا', 'دانة، الكويت'],
      ['قشرة الرأس قلّت بشكل ملحوظ', 'أحمد، جدة'],
      ['يستاهل كل فلس، جودة ممتازة', 'شيخة، أبوظبي'],
      ['ماء ألطف على البشرة الحساسة', 'العنود، الرياض'],
      ['طلبته لكل بيوت العائلة', 'فاطمة، المنامة'],
      ['ضغط الماء أقوى والاستحمام ألذّ', 'يوسف، الكويت'],
      ['بشرة أطفالي ما عادت تتحسّس', 'أمل، الكويت'],
      ['شكله أنيق ويوصل بسرعة', 'ريم، دبي']
    ] : [
      ['My skin is visibly softer', 'Nora, Kuwait'],
      ['Best thing I bought for my shower', 'Sara, Kuwait'],
      ['A clear difference in week one', 'Lina, Dubai'],
      ['Less hair fall, cleaner water', 'Abdullah, Riyadh'],
      ['Easy to install, great result', 'Maryam, Doha'],
      ["I won't shower without it now", 'Haya, Kuwait'],
      ['The chlorine smell is gone', 'Dana, Kuwait'],
      ['My dandruff dropped noticeably', 'Ahmad, Jeddah'],
      ['Worth every penny, great quality', 'Shaikha, Abu Dhabi'],
      ['Gentler water for sensitive skin', 'Al Anoud, Riyadh'],
      ['Ordered one for the whole family', 'Fatima, Manama'],
      ['Stronger pressure, better showers', 'Yousef, Kuwait'],
      ["My kids' skin stopped reacting", 'Amal, Kuwait'],
      ['Looks elegant and shipped fast', 'Reem, Dubai']
    ];
    function chip(r) {
      return '<span class="purla-rating__chip"><span class="s">★★★★★</span> <b>' + r[0] + '</b> <span class="n">— ' + r[1] + '</span></span>';
    }
    var chips = reviews.map(chip).join('');
    var el = document.createElement('section');
    el.className = 'purla-rating';
    el.setAttribute('dir', ar ? 'rtl' : 'ltr');
    el.innerHTML =
      '<div class="purla-rating__score">' +
        '<div class="purla-rating__num" data-target="' + score + '">0.0</div>' +
        '<div class="purla-rating__stars" aria-hidden="true"><span>★</span><span>★</span><span>★</span><span>★</span><span>★</span></div>' +
        '<div class="purla-rating__count">' + countLine + '</div>' +
      '</div>' +
      '<div class="purla-rating__ticker"><div class="purla-rating__track">' + chips + chips + '</div></div>';
    host.parentNode.insertBefore(el, host);
    host.style.display = 'none';               // hide the cartoon banner

    var num = el.querySelector('.purla-rating__num');
    function count() {
      if (reduce) { num.textContent = score.toFixed(1); return; }
      var dur = 1200, start = null;
      function fr(t) {
        if (!start) start = t;
        var p = Math.min((t - start) / dur, 1), e = 1 - Math.pow(1 - p, 3);
        num.textContent = (e * score).toFixed(1);
        if (p < 1) requestAnimationFrame(fr);
      }
      requestAnimationFrame(fr);
      setTimeout(function () { num.textContent = score.toFixed(1); }, dur + 60);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { io.disconnect(); count(); }
      }, { threshold: 0.3 });
      io.observe(el);
    } else { count(); }
    setTimeout(function () { if (num.textContent === '0.0') count(); }, 3500);
  }

  /* ── 7 · UPSELL — cross-sell the shower steamers under the steamer video ──
     A tidy product card injected after shopable_video_UeLeiL: image, name,
     price, and an add-to-cart button (AJAX). Product pages only, and never
     on the steamers' own page. */
  function initUpsell() {
    if (!/\/products\//.test(location.pathname)) return;
    if (/shower-steamers-gift-set/.test(location.pathname)) return;
    var host = document.querySelector('[id*="shopable_video_UeLeiL"]');
    if (!host || host.dataset.purlaUpsellDone) return;
    host.dataset.purlaUpsellDone = '1';
    var ar = (document.documentElement.lang || '').indexOf('ar') === 0;

    fetch('/products/shower-steamers-gift-set.js').then(function (r) { return r.json(); }).then(function (p) {
      if (!p || !p.available) return;
      var variant = p.variants && p.variants[0] && p.variants[0].id;
      if (!variant) return;
      var img = p.featured_image || (p.images && p.images[0]) || '';
      if (img && img.indexOf('//') === 0) img = 'https:' + img;
      var price = 'KD ' + (p.price / 100).toFixed(3);
      var eyebrow = ar ? 'أكمِل طقس استحمامك' : 'Complete your ritual';
      var cta = ar ? 'أضِف للسلة' : 'Add to cart';

      var el = document.createElement('div');
      el.className = 'purla-upsell';
      el.setAttribute('dir', ar ? 'rtl' : 'ltr');
      el.innerHTML =
        '<a class="purla-upsell__media" href="/products/' + p.handle + '" aria-label="' + p.title + '"><img src="' + img + '" alt="' + p.title + '"></a>' +
        '<div class="purla-upsell__body">' +
          '<p class="purla-upsell__eyebrow">' + eyebrow + '</p>' +
          '<a class="purla-upsell__name" href="/products/' + p.handle + '" style="text-decoration:none">' + p.title + '</a>' +
          '<div class="purla-upsell__price">' + price + '</div>' +
        '</div>' +
        '<button type="button" class="purla-upsell__btn">' + cta + '</button>';
      host.parentNode.insertBefore(el, host.nextSibling);

      var btn = el.querySelector('.purla-upsell__btn');
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        btn.disabled = true; btn.textContent = '…';
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant, quantity: 1 }] })
        }).then(function (r) { return r.json(); }).then(function () {
          btn.textContent = ar ? '✓ أُضيف للسلة' : '✓ Added';
          btn.classList.add('is-added'); btn.disabled = false;
          document.dispatchEvent(new CustomEvent('cart:build'));   // nudge theme cart badge
        }).catch(function () { btn.textContent = cta; btn.disabled = false; });
      });
    }).catch(function () {});
  }

  function boot() { initBoomerang(); initMagnetic(); initTrust(); initReveal(); initProof(); initRatingBand(); initUpsell(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('load', boot);       // re-run after full load (video ready)
})();
